/// <reference path="./env.d.ts" />
// ============================================
// @gs/config — Variables d'environnement GS (Zod)
// ============================================
import { z } from 'zod';
// ─── Schéma serveur ───────────────────────────────
const serverEnvSchema = z.object({
    // Azure SQL (GS_Apps) — obligatoire pour toutes les apps
    AZURE_SQL_SERVER: z.string().min(1),
    AZURE_SQL_DATABASE: z.string().min(1).default('GS_Apps'),
    AZURE_SQL_USER: z.string().min(1),
    AZURE_SQL_PASSWORD: z.string().min(1),
    // Azure AD — obligatoire
    AZURE_AD_CLIENT_ID: z.string().uuid(),
    AZURE_AD_TENANT_ID: z.string().uuid(),
    AZURE_AD_REDIRECT_URI: z.string().url().optional(),
    // Azure Blob Storage — obligatoire pour les apps avec upload
    AZURE_STORAGE_CONNECTION_STRING: z.string().min(1),
    AZURE_STORAGE_CONTAINER: z.string().min(1).default('gs-uploads'),
    // SOFI — optionnel (requis uniquement pour l'ETL GS-Analytics)
    SOFI_SQL_SERVER: z.string().min(1).optional(),
    SOFI_SQL_DATABASE: z.string().min(1).optional().default('Signal_Location_XPA'),
    SOFI_SQL_USER: z.string().min(1).optional(),
    SOFI_SQL_PASSWORD: z.string().min(1).optional(),
    NODE_ENV: z.enum(['development', 'production', 'test']).default('development'),
});
// ─── Schéma ETL-only (SOFI requis) ───────────────────────────
// Utilisé par l'ETL GS-Analytics — valide que SOFI est configuré
const sofiEnvSchema = z.object({
    SOFI_SQL_SERVER: z.string().min(1, 'SOFI_SQL_SERVER manquant — vérifier .env'),
    SOFI_SQL_DATABASE: z.string().min(1).default('Signal_Location_XPA'),
    SOFI_SQL_USER: z.string().min(1, 'SOFI_SQL_USER manquant — vérifier .env'),
    SOFI_SQL_PASSWORD: z.string().min(1, 'SOFI_SQL_PASSWORD manquant — vérifier .env'),
});
const uuidOrEmpty = z.union([z.string().uuid(), z.literal('')]);
const optionalUrl = z.preprocess((v) => (v === '' || v === undefined ? undefined : v), z.string().url().optional());
// ─── Schéma client (Vite) ────────────────────────
/** Schéma client Vite — exporté pour `@gs/config/browser` uniquement */
export const clientEnvSchema = z.object({
    VITE_AZURE_AD_CLIENT_ID: uuidOrEmpty.default(''),
    VITE_AZURE_AD_TENANT_ID: uuidOrEmpty.default(''),
    VITE_AZURE_AD_REDIRECT_URI: optionalUrl,
    VITE_API_URL: z.preprocess((v) => (typeof v === 'string' && v.trim() !== '' ? v.trim() : 'http://localhost:3001'), z.string().url()),
});
let _serverConfig = null;
function formatZodIssues(err) {
    return err.issues.map((e) => `${e.path.join('.') || '(root)'}: ${e.message}`).join('; ');
}
/**
 * Valide `process.env` pour les backends Node.
 * À appeler au démarrage du serveur Express.
 */
export function getServerConfig() {
    if (_serverConfig)
        return _serverConfig;
    const result = serverEnvSchema.safeParse(process.env);
    if (!result.success) {
        throw new Error(`[gs/config] Variables d'environnement invalides : ${formatZodIssues(result.error)}`);
    }
    _serverConfig = result.data;
    return _serverConfig;
}
/**
 * Retourne la configuration SOFI validée.
 * À appeler uniquement depuis l'ETL GS-Analytics (apps/analytics/server/etl/).
 * Lance une erreur explicite si SOFI_SQL_* ne sont pas dans le .env.
 */
export function getSofiConfig() {
    const result = sofiEnvSchema.safeParse(process.env);
    if (!result.success) {
        throw new Error(`[gs/config] SOFI non configuré : ${formatZodIssues(result.error)}\n` +
            `Récupérer SOFI_SQL_PASSWORD dans Azure App Service → gs-inventaire → Configuration.`);
    }
    return {
        server: result.data.SOFI_SQL_SERVER,
        database: result.data.SOFI_SQL_DATABASE,
        user: result.data.SOFI_SQL_USER,
        password: result.data.SOFI_SQL_PASSWORD,
        port: 1433,
        options: { encrypt: false, trustServerCertificate: true },
    };
}
/**
 * Configuration agrégée pour Node (Azure SQL, storage, etc.).
 * Sans `import.meta` — sûr pour bundle CJS (esbuild serveur).
 * Pour le navigateur : `import { getConfig } from '@gs/config/browser'`.
 */
export function getConfig() {
    const s = getServerConfig();
    return {
        sql: {
            server: s.AZURE_SQL_SERVER,
            database: s.AZURE_SQL_DATABASE,
            user: s.AZURE_SQL_USER,
            password: s.AZURE_SQL_PASSWORD,
            port: 1433,
            options: { encrypt: true, trustServerCertificate: false },
        },
        auth: {
            clientId: s.AZURE_AD_CLIENT_ID,
            tenantId: s.AZURE_AD_TENANT_ID,
            redirectUri: s.AZURE_AD_REDIRECT_URI ?? 'http://localhost:5173',
            scopes: ['User.Read', 'openid', 'profile', 'email'],
        },
        storage: {
            connectionString: s.AZURE_STORAGE_CONNECTION_STRING,
            containerName: s.AZURE_STORAGE_CONTAINER,
        },
        apiUrl: process.env.VITE_API_URL ?? 'http://localhost:3001',
        nodeEnv: s.NODE_ENV,
    };
}
/** Liste des 11 régions GS */
export const GS_REGIONS = [
    'Montréal',
    'Sainte-Catherine',
    'Saint-Jérôme',
    'Joliette',
    'Trois-Rivières',
    'Drummondville',
    'Sherbrooke',
    'Rouyn-Noranda',
    'Jonquière',
    'Gatineau',
    'Lévis',
];
/** Abréviations des points de service (codes internes) */
export const SP_ABBREVIATIONS = {
    Montréal: 'SSM',
    'Sainte-Catherine': 'SM',
    'Saint-Jérôme': 'SL',
    Joliette: 'SLA',
    'Trois-Rivières': 'SMA',
    Drummondville: 'SD',
    Sherbrooke: 'SE',
    Gatineau: 'SGA',
    'Rouyn-Noranda': 'SAI',
    Jonquière: 'SSA',
    Lévis: 'SQB',
};
/** Catégories d'articles GS */
export const GS_CATEGORIES = ['Signalisation', 'EPI', 'Lettrage'];
/** Couleurs (hex) pour graphiques / légendes par catégorie — DS v2.0 */
export const GS_CATEGORY_COLORS = {
    Signalisation: '#f97316',
    EPI: '#3b82f6',
    Lettrage: '#8b5cf6',
};
/** Mois de début et fin de la saison haute (avril → octobre) */
export const GS_SEASONS = { highStart: 4, highEnd: 10 };
//# sourceMappingURL=index.js.map